import { useEffect, useState } from 'react'
import { X, Heart, Copy, Check, Landmark, Loader2 } from 'lucide-react'
import { useDonsModal } from '@/hooks/useDonsModal'
import { useContent } from '@/hooks/useContent'
import { OPERATEURS } from '@/content/operateurs'

export default function DonsModal() {
  const { isOpen, close } = useDonsModal()
  const [copied, setCopied] = useState<string | null>(null)
  const { dons, contenuProvisoire, etatContenu } = useContent()
  // Premier passage, serveur pas encore répondu : on n'affiche pas des numéros peut-être périmés.
  const enAttente = contenuProvisoire && etatContenu === 'chargement'
  const { mobileMoney, compteBancaire } = dons

  // Ferme sur Échap et bloque le scroll de la page pendant que la modale est ouverte
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen, close])

  if (!isOpen) return null

  const handleCopy = async (numero: string) => {
    try {
      await navigator.clipboard.writeText(numero)
      setCopied(numero)
      setTimeout(() => setCopied((current) => (current === numero ? null : current)), 1800)
    } catch {
      // Presse-papier indisponible (permissions, contexte non sécurisé…) — silencieux, le numéro reste affichable/copiable à la main.
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-cmp-navy-dark/60 backdrop-blur-sm"
      onClick={close}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="dons-modal-title"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-white rounded-3xl rounded-tl-4xl shadow-2xl p-6 md:p-8 max-h-[90svh] overflow-y-auto"
      >
        <button
          type="button"
          onClick={close}
          aria-label="Fermer"
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-cmp-navy transition-colors"
        >
          <X size={18} />
        </button>

        <div className="w-12 h-12 bg-cmp-sky-light rounded-full flex items-center justify-center ring-1 ring-cmp-gold-light/30">
          <Heart size={20} className="text-cmp-gold" />
        </div>

        <h2 id="dons-modal-title" className="text-xl font-bold text-cmp-navy mt-4">
          Soutenir l'église
        </h2>
        <p className="text-gray-500 text-sm leading-relaxed mt-1.5">
          Vos dons financent le ministère et étendent la mission. Choisissez un moyen ci-dessous et
          copiez le numéro en un clic.
        </p>

        {enAttente ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
            <Loader2 size={16} className="animate-spin" />
            Chargement des numéros…
          </div>
        ) : (
          <>
            <div className="mt-6 space-y-3">
              {mobileMoney.map(({ id, operateur, libelle, numero, titulaire }) => (
                <div
                  key={id}
                  className="flex items-center gap-3 bg-cmp-cream rounded-2xl p-4 ring-1 ring-cmp-navy/6"
                >
                  <div className={`w-10 h-10 rounded-full ${OPERATEURS[operateur].badgeClass} flex items-center justify-center shrink-0 text-white font-bold text-xs`}>
                    {libelle.slice(0, 2).toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-cmp-navy uppercase tracking-wide">{libelle}</p>
                    <p className="text-base font-bold text-cmp-navy tabular-nums truncate">{numero}</p>
                    {titulaire && <p className="text-xs text-gray-400 truncate">{titulaire}</p>}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleCopy(numero)}
                    className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition-colors active:scale-95 ${
                      copied === numero
                        ? 'bg-green-600 text-white'
                        : 'bg-white text-cmp-navy ring-1 ring-cmp-navy/15 hover:bg-cmp-sky-light'
                    }`}
                  >
                    {copied === numero ? (
                      <>
                        <Check size={13} />
                        Copié
                      </>
                    ) : (
                      <>
                        <Copy size={13} />
                        Copier
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>

            <p className="text-cmp-gold text-xs font-bold uppercase tracking-widest mt-6 mb-3">
              Depuis l'étranger
            </p>
            <div className="flex items-start gap-3 bg-cmp-cream rounded-2xl p-4 ring-1 ring-cmp-navy/6">
              <div className="w-10 h-10 rounded-full bg-cmp-navy flex items-center justify-center shrink-0">
                <Landmark size={17} className="text-white" />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-cmp-navy uppercase tracking-wide">{compteBancaire.banque}</p>
                <p className="text-sm font-bold text-cmp-navy tabular-nums break-all mt-0.5">{compteBancaire.numero}</p>
                {compteBancaire.titulaire && <p className="text-xs text-gray-400 truncate">{compteBancaire.titulaire}</p>}
              </div>

              <button
                type="button"
                onClick={() => handleCopy(compteBancaire.numero)}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition-colors active:scale-95 ${
                  copied === compteBancaire.numero
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-cmp-navy ring-1 ring-cmp-navy/15 hover:bg-cmp-sky-light'
                }`}
              >
                {copied === compteBancaire.numero ? (
                  <>
                    <Check size={13} />
                    Copié
                  </>
                ) : (
                  <>
                    <Copy size={13} />
                    Copier
                  </>
                )}
              </button>
            </div>
          </>
        )}

        <p className="text-xs text-gray-400 text-center mt-6">
          Un reçu ou une question ? Écrivez-nous sur WhatsApp après votre don.
        </p>
      </div>
    </div>
  )
}
